import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ThemePreference } from '@db-models/worker-db.model';
import { PartnerService } from '@feature-services/partner.service';
import { WorkerService } from '@feature-services/worker.service';
import { filter } from 'rxjs/operators';
import { applyChartThemeDefaults } from './chart-theme.util';

export type ThemeResolved = 'dark' | 'light';

/**
 * 3-state theme preference controller.
 *
 * Stored preference in localStorage['cd_theme'] is one of:
 *   'system' — follows OS prefers-color-scheme live (explicit opt-in, NOT the default)
 *   'dark'   — always dark
 *   'light'  — always light (the DEFAULT when unset, so the release is non-disruptive:
 *              an existing OS-dark user is NOT auto-switched to dark — dark is opt-in)
 *
 * The RESOLVED theme (what actually paints) is still 'dark' | 'light'.
 * The actual colours live in CSS — `:root.dark` overrides in
 * `src/app/shared/scss/_dark-theme.scss`. This service only toggles the
 * `dark` class on <html> and persists the preference.
 *
 * Persistence uses a PLAIN (unencrypted) localStorage key on purpose: the
 * no-flash inline script in index.html runs before Angular/crypto load and
 * must be able to read the same value synchronously. Keep this key and the
 * inline script in sync.
 */
@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  /** Plain localStorage key — mirrored by the inline script in index.html. */
  static readonly STORAGE_KEY = 'cd_theme';
  private static readonly DARK_CLASS = 'dark';

  /**
   * End-user surfaces that are LIGHT-ONLY regardless of the partner's theme
   * preference: the appointment / event / customer landing pages, and the smart
   * widget served on the dashboard (the `/survey` route). A customer — or a
   * super-admin previewing their own booking — must never inherit the dashboard's
   * dark theme on these pages. EVERYTHING ELSE (login, onboarding, the dashboard
   * itself, …) follows the normal rules: the UI preference + the super-admin beta gate.
   *
   * Mirrored by the no-flash inline script in index.html — keep the two in sync.
   * Matches TOP-LEVEL routes only, so the dashboard's own /app/appointments,
   * /app/events, /app/customers (and /app/settings/smart-widget*) are unaffected.
   */
  static isLightOnlyPath(pathname: string): boolean {
    return /^\/(appointments|events|customers|survey)(\/|$)/.test(pathname);
  }

  private workerService = inject(WorkerService);
  private partnerService = inject(PartnerService);
  private router = inject(Router);

  // Declared BEFORE `resolved`: JS field initializers run in declaration order, and
  // `resolved`'s initializer calls resolve() which reads this. (Previously it sat AFTER
  // `resolved`, so a 'system' preference on a dark OS briefly initialized to 'light'
  // until the constructor corrected it — harmless but fragile if fields are reordered.)
  private mediaQuery = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

  /** The stored 3-state preference. */
  preference = signal<ThemePreference>(this.readStoredPreference());

  /** The resolved applied theme ('dark' | 'light'). */
  private resolved = signal<ThemeResolved>(this.resolve(this.preference()));

  /** Convenience computed — true when the applied theme is dark. */
  readonly isDark = computed(() => this.resolved() === 'dark');

  private mediaQueryListener = (e: MediaQueryListEvent): void => {
    if (this.preference() === 'system') {
      const applied: ThemeResolved = e.matches ? 'dark' : 'light';
      this.resolved.set(applied);
      this.applyTheme(applied);
    }
  };

  constructor() {
    // The inline script has (usually) already applied the class pre-boot; this
    // re-applies idempotently so the DOM always matches the signal — covers the
    // case where the inline script is absent (e.g. a stripped index.html).
    const initial = this.resolve(this.preference());
    this.resolved.set(initial);
    this.applyTheme(initial);

    // Live-track OS changes when preference === 'system'.
    this.mediaQuery?.addEventListener('change', this.mediaQueryListener);

    // Some surfaces are light-only (see resolve()), so the applied theme depends on the
    // ROUTE. Re-resolve on every navigation so moving between the dashboard and a
    // light-only page (landing / smart-widget survey) via SPA routing flips it correctly.
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.reevaluate());

    // Whenever preference changes, re-resolve and re-apply.
    effect(() => {
      const pref = this.preference();
      const applied = this.resolve(pref);
      this.resolved.set(applied);
      this.applyTheme(applied);
    });
  }

  /**
   * Set the preference directly (called by the dropdown).
   * Persists to localStorage and immediately applies the resolved theme
   * so the change is synchronously visible (the effect() also fires
   * on the next Angular scheduler tick for template bindings).
   */
  setPreference(pref: ThemePreference): void {
    const applied = this.resolve(pref);
    this.preference.set(pref);
    this.resolved.set(applied);
    this.applyTheme(applied);
    this.persist(pref);
    this.persistToServer(pref);
  }

  /**
   * Seed the preference from the logged-in worker's SERVER value — called after
   * login once the worker payload is in localStorage.
   *
   * The server value deliberately WINS over the local cache: that is the whole
   * point of per-worker persistence (log in on another browser and your theme
   * follows you). Also refreshes the no-flash localStorage cache so the next
   * pre-boot paint is already correct. Does NOT write back to the server — this
   * value came FROM the server.
   */
  hydrateFromWorker(pref: ThemePreference | null | undefined): void {
    if (pref === 'system' || pref === 'dark' || pref === 'light') {
      this.preference.set(pref);
    }
    // Runs UNCONDITIONALLY, outside the validity check above: a worker with no
    // server-side preference can still be carrying a stale local 'dark' that must be
    // coerced, so returning early on an absent server value would skip the gate.
    this.enforceBetaGate();
    // ONE write, of the EFFECTIVE preference. Persisting before the gate wrote 'dark'
    // and then 'light' on every single login for a coerced worker — and persist()
    // swallows its own failures, so a failed second write left storage disagreeing with
    // the signal. index.html's no-flash script paints from storage, so that disagreement
    // is visible on the next load.
    this.persist(this.preference());
    this.reevaluate();
  }

  /**
   * Fire-and-forget server persist. The local cache already gave instant UX, so a
   * failed sync only means the choice isn't durable across devices — never surface
   * an error for a theme toggle. No-op when logged out (e.g. the login page).
   */
  private persistToServer(pref: ThemePreference): void {
    const worker = this.workerService.getLoggedInWorkerFromLocalStorage();
    if (!worker?.uuid) {
      return;
    }
    // Patch the cached worker FIRST (optimistic, local) so a same-session reload —
    // which re-hydrates from this cache — reflects the choice even if the server PUT
    // below fails. Without this the reload silently reverts the theme.
    this.workerService.patchLoggedInWorkerTheme(pref);
    this.workerService.updateWorkerTheme(worker.uuid, pref).subscribe({
      error: (error: HttpErrorResponse) =>
        console.warn('[ColorSchemeService] theme preference not persisted server-side:', error?.status),
    });
  }

  /**
   * Resolve a preference to the theme that actually paints.
   *
   * Deliberately does NOT consult the partner's super-admin status. The beta gate is
   * ENFORCED at the points where the active identity changes (see
   * enforcePartnerThemeGate) rather than DERIVED on every resolve, because resolve()
   * also runs while logged out — on the login screen there is no cached partner, and a
   * gate evaluated there cannot tell "not a super admin" from "nobody is logged in".
   * Deriving it here forced the login screen back to light and defeated the theme that
   * LocalStorageService.clear() deliberately preserves across logout.
   */
  private resolve(pref: ThemePreference): ThemeResolved {
    // END-USER LIGHT-ONLY GATE: the customer-facing landing pages and the smart widget
    // served on the dashboard (/survey) are always light, even for a logged-in super-admin.
    // Everything else falls through to the stored preference.
    if (typeof location !== 'undefined' && ColorSchemeService.isLightOnlyPath(location.pathname)) {
      return 'light';
    }
    if (pref === 'system') {
      return this.mediaQuery?.matches ? 'dark' : 'light';
    }
    return pref;
  }

  /**
   * Re-resolve + re-apply the current preference. Called on every navigation because
   * some routes are light-only (see resolve()). Does not change the stored preference.
   */
  reevaluate(): void {
    const applied = this.resolve(this.preference());
    this.resolved.set(applied);
    this.applyTheme(applied);
  }

  /**
   * BETA GATE — dark mode is limited to super admins while the beta runs.
   *
   * Called only from hydrateFromWorker, which is the single point where the active
   * identity becomes known: login, reload while signed in, and — because switching
   * partners re-runs the auth callback, which re-emits userIsLoggedInEvent once the new
   * partner is cached — an in-place partner switch too. The header's partnerSwitchedEvent
   * is NOT a viable second call site: AuthService.handlePartnerSwitch clears localStorage
   * one line before emitting it, so any gate there reads a wiped partner.
   *
   * FAILS CLOSED. Anyone logged in who is not a CONFIRMED super admin resolves to light,
   * because getPartnerFromLocalStorage() has three outcomes, not two: the partner, null
   * when nothing is cached, and *undefined* while it refreshes a cache missing
   * internal_token (PartnerService falls through that branch without returning). Testing
   * `partner && …` treated that undefined as "logged out" and let a non-super-admin keep
   * dark for the whole session.
   *
   * Skipped entirely when logged out — no identity to gate on, so the stored preference
   * stands. That is what keeps the login screen on the theme the user logged out with.
   *
   * Mutates the local preference only and never PUTs, so the worker's own preference
   * survives on the server for their next super-admin session.
   *
   * Remove this method and its call site at GA.
   */
  private enforceBetaGate(): void {
    if (!this.workerService.getLoggedInWorkerFromLocalStorage()?.uuid) {
      return;
    }
    if (this.partnerService.getPartnerFromLocalStorage()?.is_super_admin === 1) {
      return;
    }
    if (this.preference() !== 'light') {
      this.preference.set('light');
    }
  }

  private applyTheme(resolved: ThemeResolved): void {
    if (typeof document === 'undefined') { return; }
    const root = document.documentElement;
    if (resolved === 'dark') {
      root.classList.add(ColorSchemeService.DARK_CLASS);
    } else {
      root.classList.remove(ColorSchemeService.DARK_CLASS);
    }
    // The .dark class is now set, so CSS vars resolve to the active theme — re-theme every
    // Chart.js canvas (their legend/tick/grid colours can't read CSS vars, so they must be
    // pushed into Chart.defaults and the live charts re-rendered). Runs on boot + toggle.
    applyChartThemeDefaults();
  }

  private readStoredPreference(): ThemePreference {
    try {
      const stored = localStorage.getItem(ColorSchemeService.STORAGE_KEY);
      if (stored === 'dark' || stored === 'light' || stored === 'system') {
        return stored;
      }
      // Unset or invalid → default to 'light' (non-disruptive; 'system' is an opt-in choice).
      return 'light';
    } catch {
      // Private-mode / storage-disabled browsers — default to light.
      return 'light';
    }
  }

  private persist(pref: ThemePreference): void {
    try {
      localStorage.setItem(ColorSchemeService.STORAGE_KEY, pref);
    } catch (err) {
      // Best-effort: private-mode / quota-exceeded browsers may block setItem.
      // The theme is applied in-session via the signal; the preference will revert
      // to the previously stored value (or 'system') on the next page load.
      console.warn('[ColorSchemeService] Failed to persist theme preference; reload will revert:', err);
    }
  }
}
